// services/LookupService.js
// Cache for the rarely-changing dropdown endpoints (users-list, branches-list, items-list).
// These are hit by almost every add/edit form, return the full collection, and change far less
// often than they are read. Cached in sessionStorage so it survives navigation but not a new tab.
import ApiService from './ApiService';

const TTL = 5 * 60 * 1000; // 5 minutes
const KEY_PREFIX = 'lookup:';

// De-dupes concurrent calls for the same url within a page load
const inFlight = new Map();

const readCache = (url) => {
  try {
    const raw = sessionStorage.getItem(KEY_PREFIX + url);
    if (!raw) return null;
    const { at, data } = JSON.parse(raw);
    if (Date.now() - at > TTL) {
      sessionStorage.removeItem(KEY_PREFIX + url);
      return null;
    }
    return data;
  } catch (e) {
    return null;
  }
};

const writeCache = (url, data) => {
  try {
    sessionStorage.setItem(KEY_PREFIX + url, JSON.stringify({ at: Date.now(), data }));
  } catch (e) {
    // sessionStorage full or unavailable — caching is best effort
  }
};

const LookupService = {
  // Returns the list payload, from cache when fresh. Throws on API failure.
  async get(url) {
    const cached = readCache(url);
    if (cached) return cached;

    if (inFlight.has(url)) return inFlight.get(url);

    const promise = (async () => {
      const response = await ApiService.request({ method: 'GET', url });
      const body = response.data;
      if (!body.status) throw new Error(body.message);
      writeCache(url, body.data);
      return body.data;
    })().finally(() => inFlight.delete(url));

    inFlight.set(url, promise);
    return promise;
  },

  // Call after a create/update/delete that invalidates a list
  clear(url) {
    if (url) {
      sessionStorage.removeItem(KEY_PREFIX + url);
      return;
    }
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith(KEY_PREFIX))
      .forEach((k) => sessionStorage.removeItem(k));
  },
};

export default LookupService;
